// ============================================================================
// Cue clipboard — copy a cue on one board (template or day) and paste it on
// any other board, in this tab or another tab of the same browser.
//
// Two kinds of thing can be on the clipboard:
//   { kind: "block", block }      — one ON-GROUND / BROADCAST / platform block
//                                   (text, sound, colour, background photo)
//   { kind: "rows", rows: [{ cue, blocks }] } — several whole rows at once
//                                   (blocks keep a span within the copied rows)
//   { kind: "row", cue, blocks }  — a whole master-cue row: the cue itself
//                                   plus every block that starts on that row,
//                                   keyed by column ("onGround", "broadcastMain",
//                                   or a platform name)
//
// It lives in localStorage so it survives switching boards and reaches other
// tabs (the "storage" event). localStorage can be unavailable (private mode,
// blocked site data), so every access is guarded and an in-memory copy is
// kept as a fallback — copying still works within the tab in that case.
//
// Photos are referenced by asset id, which is valid on every board of the
// same site, so a pasted cue shows the same image without re-uploading.
// ============================================================================
const { useEffect, useState } = React;

const KEY = "rundown:cue-clipboard";
let memory = null;
const listeners = new Set();
function notify(v) { for (const fn of [...listeners]) { try { fn(v); } catch (e) {} } }

function sanitizeBlock(b) {
  return {
    image: String((b && b.image) || ""),
    sound: String((b && b.sound) || ""),
    color: String((b && b.color) || "warmgray"),
    bg: String((b && b.bg) || ""),
    span: Math.max(1, Math.min(500, parseInt(b && b.span, 10) || 1)),
  };
}

function sanitizeBlocks(raw) {
  const blocks = {};
  if (raw && typeof raw === "object") {
    for (const [col, list] of Object.entries(raw)) {
      if (!/^[^\u0000-\u001f]{1,60}$/.test(col) || !Array.isArray(list)) continue;
      blocks[col] = list.slice(0, 20).map(sanitizeBlock);
    }
  }
  return blocks;
}

function sanitizeCue(c) {
  return {
    name: String((c && c.name) || ""),
    icon: String((c && c.icon) || "glove"),
    color: String((c && c.color) || "black"),
    bg: String((c && c.bg) || ""),
    boutNumber: String((c && c.boutNumber) || ""),
    redCorner: String((c && c.redCorner) || ""),
    blueCorner: String((c && c.blueCorner) || ""),
  };
}

// accept only the two known shapes, with only known fields — the clipboard
// can be written by another tab, so treat it like any other outside input
function validate(v) {
  if (!v || typeof v !== "object") return null;
  if (v.kind === "block" && v.block && typeof v.block === "object") {
    return { kind: "block", block: sanitizeBlock(v.block), from: String(v.from || ""), at: Number(v.at) || 0 };
  }
  if (v.kind === "row" && v.cue && typeof v.cue === "object") {
    return { kind: "row", cue: sanitizeCue(v.cue), blocks: sanitizeBlocks(v.blocks), from: String(v.from || ""), at: Number(v.at) || 0 };
  }
  if (v.kind === "rows" && Array.isArray(v.rows) && v.rows.length) {
    const rows = v.rows.slice(0, 500).filter((r) => r && r.cue && typeof r.cue === "object")
      .map((r) => ({ cue: sanitizeCue(r.cue), blocks: sanitizeBlocks(r.blocks) }));
    if (rows.length) return { kind: "rows", rows, from: String(v.from || ""), at: Number(v.at) || 0 };
  }
  return null;
}

function readClipboard() {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw) return validate(JSON.parse(raw));
  } catch (e) { /* storage unavailable — fall back to memory */ }
  return memory;
}

function writeClipboard(value) {
  const v = validate({ ...value, at: Date.now() });
  if (!v) return;
  memory = v;
  try { window.localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* memory copy is enough */ }
  notify(v);
}

function clearClipboard() {
  memory = null;
  try { window.localStorage.removeItem(KEY); } catch (e) {}
  notify(null);
}

function copyBlock(block, from) { writeClipboard({ kind: "block", block, from }); }

// a master cue plus every block that starts on its row, in every column
function copyRow(board, cueIndex, from) {
  const cue = board.masterCues[cueIndex];
  if (!cue) return;
  const blocks = {};
  const take = (col, list) => {
    const here = (list || []).filter((b) => b.start === cueIndex);
    if (here.length) blocks[col] = here;
  };
  take("onGround", board.onGround);
  take("broadcastMain", board.broadcastMain);
  for (const [name, list] of Object.entries(board.platforms || {})) take(name, list);
  writeClipboard({ kind: "row", cue, blocks, from });
}

// several master-cue rows (by index) with the blocks that start on them. When
// the rows are back-to-back a block keeps its span (cut at the last copied
// row); otherwise each block is one row tall.
function copyRows(board, indices, from) {
  const idx = [...new Set(indices)].filter((i) => board.masterCues[i]).sort((a, b) => a - b);
  if (!idx.length) return;
  if (idx.length === 1) { copyRow(board, idx[0], from); return; }
  const contiguous = idx[idx.length - 1] - idx[0] === idx.length - 1;
  const last = idx[idx.length - 1];
  const rows = idx.map((i) => {
    const blocks = {};
    const take = (col, list) => {
      const here = (list || []).filter((b) => b.start === i).map((b) => ({ ...b, span: contiguous ? Math.min(b.span || 1, last - i + 1) : 1 }));
      if (here.length) blocks[col] = here;
    };
    take("onGround", board.onGround);
    take("broadcastMain", board.broadcastMain);
    for (const [name, list] of Object.entries(board.platforms || {})) take(name, list);
    return { cue: board.masterCues[i], blocks };
  });
  writeClipboard({ kind: "rows", rows, from });
}

// the rows a clipboard entry would paste (one for "row", several for "rows")
function clipRows(clip) {
  if (!clip) return null;
  if (clip.kind === "rows") return clip.rows;
  if (clip.kind === "row") return [{ cue: clip.cue, blocks: clip.blocks }];
  return null;
}

function useCueClipboard() {
  const [clip, setClip] = useState(() => readClipboard());
  useEffect(() => {
    const onLocal = (v) => setClip(v);
    const onStorage = (e) => { if (e.key === KEY || e.key === null) setClip(readClipboard()); };
    listeners.add(onLocal);
    window.addEventListener("storage", onStorage);
    // catch anything copied between first render and this effect running
    setClip(readClipboard());
    return () => { listeners.delete(onLocal); window.removeEventListener("storage", onStorage); };
  }, []);
  return clip;
}

// what a clipboard entry offers to a given column when pasting a single block:
// a copied block as-is, or — from a copied row — that row's block in the same
// column (falling back to its first block anywhere, so a row copied from a
// board with different platform names still pastes something useful)
function blockForColumn(clip, col) {
  if (!clip) return null;
  if (clip.kind === "block") return clip.block;
  const src = clip.kind === "rows" ? clip.rows[0] : clip;
  const list = (src.blocks && (src.blocks[col] || Object.values(src.blocks)[0])) || [];
  return list[0] ? { ...list[0], span: 1 } : null;
}

// insert an empty row at `idx`: blocks at or below it move down one row, and
// a block that spans across the insertion point grows to keep covering it
function shiftForInsert(blocks, idx, count = 1) {
  return (blocks || []).map((b) => {
    const end = b.start + (b.span || 1) - 1;
    if (b.start >= idx) return { ...b, start: b.start + count };
    if (end >= idx) return { ...b, span: (b.span || 1) + count };
    return b;
  });
}

export { readClipboard, writeClipboard, clearClipboard, copyBlock, copyRow, copyRows, clipRows, useCueClipboard, blockForColumn, shiftForInsert, validate as validateClipboard };
